import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Sparkles, Lock, Mail } from 'lucide-react';
import { track } from '@/lib/analytics';

const Auth = () => {
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [codeValid, setCodeValid] = useState<boolean | null>(null);
  const [codeChecking, setCodeChecking] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmSent, setConfirmSent] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [magicLoading, setMagicLoading] = useState(false);
  const { signUp, signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const rawRedirect = (location.state as { from?: string })?.from || '/dashboard';
  const redirectTo = rawRedirect.startsWith('/') && !rawRedirect.startsWith('//') ? rawRedirect : '/dashboard';

  // Parse ?code= from URL and pre-validate
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlCode = params.get('code');
    if (urlCode) {
      setInviteCode(urlCode.toUpperCase());
      setMode('signup');
      validateCode(urlCode.toUpperCase());
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const validateCode = async (code: string) => {
    if (!code.trim()) { setCodeValid(null); return; }
    setCodeChecking(true);
    const { data } = await supabase.rpc('validate_access_code' as never, { p_code: code.trim().toUpperCase() });
    setCodeValid(!!data);
    setCodeChecking(false);
  };

  const handleCodeBlur = () => validateCode(inviteCode);

  // Magic-link sign-in: email-only, no password. Supabase emails the user a
  // one-time link that signs them in when clicked. Only allowed for existing
  // accounts (shouldCreateUser: false) — new users still need an invite code
  // and the signup flow.
  const handleMagicLink = async () => {
    setError('');
    if (!email.trim()) {
      setError('Enter your email above first.');
      return;
    }
    setMagicLoading(true);
    const origin = window.location.origin;
    const redirectPath = redirectTo.startsWith('/') ? redirectTo : '/dashboard';
    const { error: otpError } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: {
        shouldCreateUser: false,
        emailRedirectTo: `${origin}${redirectPath}`,
      },
    });
    setMagicLoading(false);
    if (otpError) {
      setError(otpError.message);
      return;
    }
    track({ event: 'sign_in', props: { method: 'magic_link' } });
    setMagicLinkSent(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (mode === 'signup') {
      if (!codeValid) {
        setError('A valid invite code is required to create an account.');
        setLoading(false);
        return;
      }
      const { error } = await signUp(email, password, displayName, inviteCode.trim().toUpperCase());
      if (error) {
        setError(error.message);
      } else {
        track({ event: 'sign_up', props: { method: 'email' } });
        setConfirmSent(true);
      }
    } else {
      const { error } = await signIn(email, password);
      if (error) {
        setError(error.message);
      } else {
        track({ event: 'sign_in', props: { method: 'email' } });
        navigate(redirectTo);
      }
    }
    setLoading(false);
  };

  if (confirmSent) {
    return (
      <div className="min-h-screen bg-journey-atmosphere flex items-center justify-center px-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-8 max-w-md w-full text-center space-y-4">
          <Sparkles className="w-12 h-12 text-primary mx-auto" aria-hidden="true" />
          <h2 className="text-2xl font-display font-bold text-foreground">Check your email</h2>
          <p className="text-muted-foreground text-sm">We sent a confirmation link to <span className="text-foreground">{email}</span>. Click it to activate your account.</p>
          <button onClick={() => { setConfirmSent(false); setMode('signin'); }} className="text-primary text-sm hover:underline">
            Back to sign in
          </button>
        </motion.div>
      </div>
    );
  }

  if (magicLinkSent) {
    return (
      <div className="min-h-screen bg-journey-atmosphere flex items-center justify-center px-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-8 max-w-md w-full text-center space-y-4">
          <Mail className="w-12 h-12 text-primary mx-auto" aria-hidden="true" />
          <h2 className="text-2xl font-display font-bold text-foreground">Sign-in link sent</h2>
          <p className="text-muted-foreground text-sm">
            We emailed a one-time sign-in link to <span className="text-foreground">{email}</span>. Click it and you&apos;re in. No password needed.
          </p>
          <p className="text-xs text-muted-foreground/70">
            The link expires in 60 minutes. Check your spam folder if you don&apos;t see it within a minute.
          </p>
          <button onClick={() => { setMagicLinkSent(false); }} className="text-primary text-sm hover:underline">
            Use a different email
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-journey-atmosphere flex items-center justify-center px-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md space-y-8">
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-display font-bold text-foreground">
            <span className="text-primary">TruePath</span>
          </h1>
          <p className="text-muted-foreground">
            {mode === 'signup' ? 'Create your account' : 'Sign in to your account'}
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          aria-label={mode === 'signup' ? 'Create account form' : 'Sign in form'}
          className="glass-card p-6 space-y-4"
        >
          {/* Sign-up tab toggle (only shown when code is already valid) */}
          {codeValid && (
            <div className="flex rounded-lg bg-secondary/50 p-0.5 gap-0.5">
              {(['signin', 'signup'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => { setMode(m); setError(''); }}
                  className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    mode === m ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {m === 'signin' ? 'Sign In' : 'Create Account'}
                </button>
              ))}
            </div>
          )}

          {mode === 'signup' && (
            <div className="space-y-1">
              <label htmlFor="displayName" className="text-sm font-medium text-foreground">Name</label>
              <input
                id="displayName"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Your name"
                autoComplete="name"
                className="w-full px-4 py-3 rounded-lg bg-secondary border border-border text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          )}

          <div className="space-y-1">
            <label htmlFor="email" className="text-sm font-medium text-foreground">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoComplete="email"
              aria-invalid={!!error}
              aria-describedby={error ? 'auth-error' : undefined}
              className="w-full px-4 py-3 rounded-lg bg-secondary border border-border text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div className="space-y-1">
            <label htmlFor="password" className="text-sm font-medium text-foreground">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              minLength={6}
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              aria-invalid={!!error}
              aria-describedby={error ? 'auth-error' : undefined}
              className="w-full px-4 py-3 rounded-lg bg-secondary border border-border text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Invite code — shown in signup mode */}
          {mode === 'signup' && (
            <div className="space-y-1">
              <label htmlFor="inviteCode" className="text-sm font-medium text-foreground flex items-center gap-1.5">
                <Lock className="w-3 h-3" aria-hidden="true" /> Invite Code
              </label>
              <div className="relative">
                <input
                  id="inviteCode"
                  type="text"
                  value={inviteCode}
                  onChange={(e) => { setInviteCode(e.target.value.toUpperCase()); setCodeValid(null); }}
                  onBlur={handleCodeBlur}
                  placeholder="XXXXXXXX"
                  maxLength={12}
                  aria-describedby="code-status"
                  className={`w-full px-4 py-3 rounded-lg bg-secondary border text-foreground placeholder:text-muted-foreground font-mono tracking-widest text-sm focus:outline-none focus:ring-2 focus:ring-primary uppercase ${
                    codeValid === true ? 'border-primary' : codeValid === false ? 'border-destructive' : 'border-border'
                  }`}
                />
              </div>
              <p id="code-status" className={`text-xs ${codeValid === true ? 'text-primary' : codeValid === false ? 'text-destructive' : 'text-muted-foreground'}`}>
                {codeChecking ? 'Checking…' : codeValid === true ? '✓ Valid invite code' : codeValid === false ? 'Invalid or expired code' : 'Enter the invite code you received'}
              </p>
            </div>
          )}

          {error && (
            <p id="auth-error" role="alert" aria-live="assertive" className="text-destructive text-sm">{error}</p>
          )}

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="submit"
            disabled={loading || (mode === 'signup' && !codeValid)}
            aria-busy={loading}
            className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-semibold text-sm glow-teal disabled:opacity-50"
          >
            {loading ? 'Loading…' : mode === 'signup' ? 'Create Account' : 'Sign In'}
          </motion.button>

          {/* Magic-link alternative — only for existing accounts (signin mode).
              One-time email link, no password. Useful when the user forgets
              their password OR just prefers not to type one. */}
          {mode === 'signin' && (
            <>
              <div className="flex items-center gap-3" aria-hidden="true">
                <div className="flex-1 h-px bg-border" />
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground/60 font-semibold">or</span>
                <div className="flex-1 h-px bg-border" />
              </div>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="button"
                onClick={handleMagicLink}
                disabled={magicLoading || !email.trim()}
                aria-busy={magicLoading}
                className="w-full py-3 rounded-xl bg-secondary text-foreground font-semibold text-sm border border-border hover:bg-secondary/70 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Mail className="w-4 h-4" aria-hidden="true" />
                {magicLoading ? 'Sending link…' : 'Email me a sign-in link'}
              </motion.button>
              <p className="text-[11px] text-muted-foreground/70 text-center">
                No password needed. We&apos;ll email you a one-time link.
              </p>
            </>
          )}

          {/* Only show "sign up" link if user already has a code */}
          {mode === 'signin' && (
            <p className="text-center text-sm text-muted-foreground">
              Have an invite code?{' '}
              <button type="button" onClick={() => { setMode('signup'); setError(''); }} className="text-primary hover:underline">
                Create account
              </button>
            </p>
          )}
          {mode === 'signup' && (
            <p className="text-center text-sm text-muted-foreground">
              Already have an account?{' '}
              <button type="button" onClick={() => { setMode('signin'); setError(''); }} className="text-primary hover:underline">
                Sign in
              </button>
            </p>
          )}
        </form>

        <p className="text-center text-xs text-muted-foreground">
          TruePath is currently in private beta. Access is by invite only.
        </p>
      </motion.div>
    </div>
  );
};

export default Auth;
